import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import '../styles/admin-common.css';

interface ExamTypeManagementPageProps {
  embedded?: boolean;
}

const DEFAULT_EXAM_TIME_SECONDS = 7800;

const ExamTypeManagementPage: React.FC<ExamTypeManagementPageProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const [examTypes, setExamTypes] = useState<any[]>([]);
  const [editingExamType, setEditingExamType] = useState<any>(null);
  const [creatingExamType, setCreatingExamType] = useState<any>(null);
  const [modalError, setModalError] = useState('');
  const [loading, setLoading] = useState(false);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const loadExamTypes = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/admin/exam-types');
      if (response.ok) {
        const result = await response.json();
        setExamTypes(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load exam types:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveExamType = async (examTypeData: any, isCreate = false) => {
    const url = isCreate ? '/admin/exam-types' : `/admin/exam-types/${examTypeData.exam_type_id}`;
    const method = isCreate ? 'POST' : 'PUT';

    const response = await apiCall(url, {
      method,
      body: JSON.stringify({
        examTypeId: examTypeData.exam_type_id,
        name: examTypeData.name,
        description: examTypeData.description,
        icon: examTypeData.icon,
        totalQuestions: Number(examTypeData.total_questions),
        examTime: Number(examTypeData.exam_time),
        passScore: Number(examTypeData.pass_score),
        allowedUsers: examTypeData.allowed_users || [],
        isActive: examTypeData.is_active !== false,
      }),
    });

    if (response.ok) {
      setModalError('');
      setEditingExamType(null);
      setCreatingExamType(null);
      loadExamTypes();
    }
  };

  const getAccessScope = (allowedUsers?: string[]) => {
    return allowedUsers && allowedUsers.length > 0 ? `제한 공개 ${allowedUsers.length}명` : '전체 공개';
  };

  const getMinutes = (examTime?: number) => {
    return Math.floor((examTime || DEFAULT_EXAM_TIME_SECONDS) / 60);
  };

  const closeExamTypeModal = () => {
    setModalError('');
    setEditingExamType(null);
    setCreatingExamType(null);
  };

  const openCreateModal = () => {
    setModalError('');
    setCreatingExamType({
      exam_type_id: '',
      name: '',
      description: '',
      icon: '',
      total_questions: '',
      exam_time: DEFAULT_EXAM_TIME_SECONDS,
      pass_score: '',
      allowed_users: [],
      is_active: true,
    });
  };

  const openEditModal = (examType: any) => {
    setModalError('');
    setEditingExamType({
      ...examType,
      allowed_users: examType.allowed_users || [],
    });
  };

  const handleSaveModal = () => {
    const isCreate = !!creatingExamType;
    const targetExamType = creatingExamType || editingExamType;
    if (!targetExamType) return;

    if (isCreate && !targetExamType.exam_type_id?.trim()) {
      setModalError('시험 유형 ID는 필수입니다.');
      return;
    }

    if (!targetExamType.name?.trim()) {
      setModalError('시험 이름은 필수입니다.');
      return;
    }

    setModalError('');
    saveExamType(targetExamType, isCreate);
  };

  useEffect(() => {
    loadExamTypes();
  }, []);

  if (!user?.is_admin) {
    return (
      <div className={`admin-page ${embedded ? 'admin-page--embedded' : ''}`}>
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요한 페이지입니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-page ${embedded ? 'admin-page--embedded' : ''}`}>
      {!embedded && (
        <header className="admin-page-title">
          <span className="admin-page-title__eyebrow">문제은행</span>
          <h1>시험 유형 관리</h1>
        </header>
      )}

      {loading && <div className="loading">불러오는 중...</div>}

      <div className="admin-section">
        <div className="admin-header">
          <div>
            <h2>시험 유형 목록</h2>
            <p>응시 가능 여부, 문항 수, 제한 공개 범위를 관리합니다.</p>
          </div>
          <button className="admin-btn primary" onClick={openCreateModal}>
            시험 유형 추가
          </button>
        </div>

        <div className="exam-type-table-wrap">
          <table className="admin-table exam-type-table">
            <thead>
              <tr>
                <th className="col-exam-name">시험 유형</th>
                <th className="col-exam-id">ID</th>
                <th className="col-total-questions">문항</th>
                <th className="col-exam-time">시간</th>
                <th className="col-pass-score">합격</th>
                <th className="col-access-scope">공개 범위</th>
                <th className="col-status">상태</th>
                <th className="col-action">작업</th>
              </tr>
            </thead>
            <tbody>
              {examTypes.map((examType) => (
                <tr key={examType.exam_type_id}>
                  <td className="col-exam-name">
                    <div className="exam-type-cell">
                      <div className="examtype-icon">
                        {examType.icon ? (
                          <img
                            src={examType.icon}
                            alt={examType.name}
                            onError={(event) => {
                              event.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span>AWS</span>
                        )}
                      </div>
                      <div className="exam-type-cell__meta">
                        <strong>{examType.name}</strong>
                        <span>{examType.description || '설명 없음'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="col-exam-id"><code>{examType.exam_type_id}</code></td>
                  <td className="col-total-questions">{examType.total_questions || '-'}문항</td>
                  <td className="col-exam-time">{getMinutes(examType.exam_time)}분</td>
                  <td className="col-pass-score">{examType.pass_score || '-'}%</td>
                  <td className="col-access-scope">{getAccessScope(examType.allowed_users)}</td>
                  <td className="col-status">
                    <span className={`admin-badge ${examType.is_active ? 'success' : 'secondary'}`}>
                      {examType.is_active ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="col-action">
                    <button className="admin-btn secondary sm" onClick={() => openEditModal(examType)}>
                      수정
                    </button>
                  </td>
                </tr>
              ))}

              {!loading && examTypes.length === 0 && (
                <tr>
                  <td colSpan={8} className="admin-empty-cell">등록된 시험 유형이 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(creatingExamType || editingExamType) && (
        <div className="exam-type-modal-overlay">
          <div className="exam-type-modal">
            <div className="exam-type-modal__header">
              <h3>{creatingExamType ? '시험 유형 추가' : '시험 유형 수정'}</h3>
              <button className="exam-type-modal__close" onClick={closeExamTypeModal} aria-label="닫기">닫기</button>
            </div>
            <div className="exam-type-modal__body">
              <ExamTypeEditor
                examType={creatingExamType || editingExamType}
                onChange={creatingExamType ? setCreatingExamType : setEditingExamType}
                isEditing={!!editingExamType}
              />
              {modalError && <p className="exam-type-modal__error">{modalError}</p>}
            </div>
            <div className="exam-type-modal__footer">
              <button className="admin-btn primary" onClick={handleSaveModal}>
                {creatingExamType ? '생성' : '저장'}
              </button>
              <button className="admin-btn secondary" onClick={closeExamTypeModal}>취소</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ExamTypeEditor: React.FC<{
  examType: any;
  onChange: (examType: any) => void;
  isEditing: boolean;
}> = ({ examType, onChange, isEditing }) => {
  const updateExamType = (updates: any) => {
    onChange({ ...examType, ...updates });
  };

  return (
    <div className="exam-type-editor exam-type-editor-grid">
      <div className="admin-form-group">
        <label>시험 유형 ID</label>
        <input
          type="text"
          className="admin-form-control"
          value={examType.exam_type_id || ''}
          onChange={(event) => updateExamType({ exam_type_id: event.target.value })}
          placeholder="예: aws-saa-c03"
          disabled={isEditing}
        />
        {isEditing && <small className="form-text">시험 유형 ID는 수정할 수 없습니다.</small>}
      </div>

      <div className="admin-form-group">
        <label>시험 이름</label>
        <input
          type="text"
          className="admin-form-control"
          value={examType.name || ''}
          onChange={(event) => updateExamType({ name: event.target.value })}
          placeholder="예: AWS Solutions Architect Associate"
        />
      </div>

      <div className="admin-form-group">
        <label>아이콘 URL</label>
        <input
          type="text"
          className="admin-form-control"
          value={examType.icon || ''}
          onChange={(event) => updateExamType({ icon: event.target.value })}
          placeholder="이미지 URL"
        />
      </div>

      <div className="admin-form-group">
        <label>설명</label>
        <textarea
          className="admin-form-control textarea"
          value={examType.description || ''}
          onChange={(event) => updateExamType({ description: event.target.value })}
          rows={2}
          placeholder="시험에 대한 설명을 입력하세요."
        />
      </div>

      <div className="exam-type-editor-grid__metrics">
        <div className="admin-form-group">
          <label>총 문항 수</label>
          <input
            type="number"
            className="admin-form-control"
            value={examType.total_questions || ''}
            onChange={(event) => updateExamType({ total_questions: event.target.value })}
          />
        </div>

        <div className="admin-form-group">
          <label>시험 시간(초)</label>
          <input
            type="number"
            className="admin-form-control"
            value={examType.exam_time || DEFAULT_EXAM_TIME_SECONDS}
            onChange={(event) => updateExamType({ exam_time: event.target.value })}
          />
        </div>

        <div className="admin-form-group">
          <label>합격 점수(%)</label>
          <input
            type="number"
            min="0"
            max="100"
            className="admin-form-control"
            value={examType.pass_score || ''}
            onChange={(event) => updateExamType({ pass_score: event.target.value })}
            placeholder="예: 72"
          />
        </div>
      </div>

      <div className="admin-form-group">
        <label>허용 사용자 이메일</label>
        <div className="allowed-users-section">
          <div className="allowed-users-list">
            {(examType.allowed_users || []).map((email: string, index: number) => (
              <div key={email} className="allowed-user-item">
                <span>{email}</span>
                <button
                  type="button"
                  onClick={() => updateExamType({
                    allowed_users: examType.allowed_users.filter((_: string, itemIndex: number) => itemIndex !== index),
                  })}
                  className="remove-user-btn"
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
          <input
            type="email"
            className="admin-form-control"
            placeholder="이메일 입력 후 Enter"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const email = event.currentTarget.value.trim();
                if (email && !examType.allowed_users?.includes(email)) {
                  updateExamType({
                    allowed_users: [...(examType.allowed_users || []), email],
                  });
                  event.currentTarget.value = '';
                }
              }
            }}
          />
          <small className="form-text">비워두면 모든 사용자에게 공개됩니다.</small>
        </div>
      </div>

      <div className="admin-form-group">
        <label className="admin-checkbox-label">
          <input
            type="checkbox"
            checked={examType.is_active !== false}
            onChange={(event) => updateExamType({ is_active: event.target.checked })}
          />
          활성 상태
        </label>
      </div>
    </div>
  );
};

export default ExamTypeManagementPage;
